import { useState } from 'react';
import { Table, Tag, Modal, Button, Space, Badge, Input, Alert, Typography, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from '../../config/axios';
import dayjs from 'dayjs';
import AppLayout from '../../layouts/app.layout';
import { handleAPIError } from '../../utils/helpers';

interface WidgetToken {
	id: number;
	label: string;
	is_active: boolean;
	last_used_at: string | null;
	created_at: string;
	created_by: string;
}

/** How long since a token last fetched, phrased for a glance. */
function lastUsed(value: string | null) {
	if (!value) return { text: 'Never used', color: '#999' };
	const hours = dayjs().diff(dayjs(value), 'hour');
	if (hours < 2) return { text: dayjs(value).format('HH:mm'), color: '#52c41a' };
	if (hours < 48) return { text: `${hours}h ago`, color: '#52c41a' };
	return { text: dayjs(value).format('DD MMM YYYY'), color: '#faad14' };
}

export default function WidgetTokens() {
	const queryClient = useQueryClient();

	const [createOpen, setCreateOpen] = useState(false);
	const [label, setLabel] = useState('');
	const [issuedToken, setIssuedToken] = useState<string | null>(null);

	const { data: tokens = [], isLoading, error } = useQuery<WidgetToken[]>({
		queryKey: ['widget-tokens'],
		queryFn: async () => {
			const res = await axios.get('/admin/super/widget-tokens', { withCredentials: true });
			return res.data.results;
		},
	});
	if (error) handleAPIError(error);

	const issue = useMutation({
		mutationFn: async (newLabel: string) => {
			const res = await axios.post(
				'/admin/super/widget-tokens',
				{ label: newLabel },
				{ withCredentials: true },
			);
			return res.data.results as WidgetToken & { token: string };
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: ['widget-tokens'] });
			setCreateOpen(false);
			setLabel('');
			// The only moment this value exists in readable form — the server
			// stores a hash, so there is no way to show it again later.
			setIssuedToken(data.token);
		},
		onError: handleAPIError,
	});

	const setActive = useMutation({
		mutationFn: async ({ id, is_active }: { id: number; is_active: boolean }) => {
			await axios.patch(`/admin/super/widget-tokens/${id}`, { is_active }, { withCredentials: true });
		},
		onSuccess: (_, variables) => {
			message.success(variables.is_active ? 'Token restored' : 'Token revoked');
			queryClient.invalidateQueries({ queryKey: ['widget-tokens'] });
		},
		onError: handleAPIError,
	});

	const columns = [
		{
			title: 'Device',
			key: 'label',
			render: (_: unknown, r: WidgetToken) => (
				<Space direction="vertical" size={0}>
					<span style={{ fontWeight: 500 }}>{r.label}</span>
					<span style={{ fontSize: 12, color: '#888' }}>Issued by {r.created_by}</span>
				</Space>
			),
		},
		{
			title: 'Status',
			key: 'status',
			render: (_: unknown, r: WidgetToken) =>
				r.is_active ? (
					<Badge status="success" text="Active" />
				) : (
					<Tag color="red">Revoked</Tag>
				),
		},
		{
			title: 'Last Used',
			key: 'last_used_at',
			render: (_: unknown, r: WidgetToken) => {
				const { text, color } = lastUsed(r.last_used_at);
				return <span style={{ fontSize: 13, color }}>{text}</span>;
			},
		},
		{
			title: 'Issued',
			dataIndex: 'created_at',
			key: 'created_at',
			render: (v: string) => dayjs(v).format('DD MMM YYYY'),
		},
		{
			title: 'Actions',
			key: 'actions',
			render: (_: unknown, r: WidgetToken) => (
				<Button
					size="small"
					danger={r.is_active}
					loading={setActive.isPending}
					onClick={() =>
						Modal.confirm({
							title: r.is_active ? 'Revoke this token?' : 'Restore this token?',
							content: r.is_active
								? `"${r.label}" will stop receiving data on its next refresh. The widget keeps showing its last figures with a "tap to fix sign-in" prompt. This cannot be undone by re-issuing the same token — you would need to create a new one and enter it on the device.`
								: `"${r.label}" will be able to fetch data again immediately.`,
							okText: r.is_active ? 'Revoke' : 'Restore',
							okButtonProps: { danger: r.is_active },
							onOk: () => setActive.mutate({ id: r.id, is_active: !r.is_active }),
						})
					}
				>
					{r.is_active ? 'Revoke' : 'Restore'}
				</Button>
			),
		},
	];

	return (
		<AppLayout
			title="Widget Tokens"
			subtitle="Credentials for home-screen widget devices"
		>
			<div className="mb-4 flex items-center justify-between">
				<span style={{ fontSize: 13, color: '#666' }}>
					Each token lets one device read the business summary. Revoke immediately if a
					phone is lost or stolen.
				</span>
				<Button type="primary" onClick={() => setCreateOpen(true)}>
					Issue Token
				</Button>
			</div>

			<Table
				dataSource={tokens}
				columns={columns}
				rowKey="id"
				loading={isLoading}
				pagination={false}
				size="small"
				rowClassName={(r) => (!r.is_active ? 'opacity-50' : '')}
			/>

			<Modal
				open={createOpen}
				title="Issue Widget Token"
				onCancel={() => {
					setCreateOpen(false);
					setLabel('');
				}}
				onOk={() => issue.mutate(label)}
				confirmLoading={issue.isPending}
				okText="Issue"
				okButtonProps={{ disabled: !label.trim() }}
			>
				<p style={{ color: '#666', marginBottom: 16, fontSize: 13 }}>
					Name the device this token is for, so you know which one to revoke later.
				</p>
				<Input
					placeholder="e.g. CEO Z Fold 7"
					value={label}
					onChange={(e) => setLabel(e.target.value)}
					onPressEnter={() => label.trim() && issue.mutate(label)}
					maxLength={100}
				/>
			</Modal>

			<Modal
				open={!!issuedToken}
				title="Token Issued"
				onCancel={() => setIssuedToken(null)}
				footer={[
					<Button key="done" type="primary" onClick={() => setIssuedToken(null)}>
						Done
					</Button>,
				]}
			>
				<Alert
					type="warning"
					showIcon
					message="Copy this now — it cannot be shown again"
					description="Only a hash is stored on the server. If you lose this value, revoke the token and issue a new one."
					style={{ marginBottom: 16 }}
				/>
				<Typography.Paragraph
					copyable={{ text: issuedToken ?? '' }}
					style={{
						background: '#f5f5f5',
						padding: '10px 12px',
						borderRadius: 6,
						fontFamily: 'monospace',
						fontSize: 13,
						wordBreak: 'break-all',
						marginBottom: 0,
					}}
				>
					{issuedToken}
				</Typography.Paragraph>
			</Modal>
		</AppLayout>
	);
}
